import { createTheme, PaletteMode, Theme } from '@mui/material';

// A dedicated theme for the side panel only (see ThemeProvider.tsx - gated
// behind appConfig.isExtension). Same shape as constants/theme.ts's getTheme
// (same Theme['palette'] type, same components consuming those tokens
// throughout AutotaskPod/Login/IconsTabs/Settings/etc.).
//
// v3 (see MSPPROC-17664): ported the actual color tokens from
// mspprocess-tech-app's EndUserVerification screen (vn.starlingTech/theme/
// tokens.ts) so the extension and the mobile app read as one product,
// rather than inventing a new palette. Notably the accent (#2D6BE4) is the
// SAME literal value in both light and dark mode there - simpler than v2's
// per-mode teal, and it's exactly why the mobile screen's icon tiles never
// had the dark-mode contrast problem VerificationFlow.tsx hit here: icons
// sit in a tinted badge, not a solid-color button (see Tile.tsx).
export const getExtensionTheme = (mode: PaletteMode): Theme => {
  const isDark = mode === 'dark';
  const accent = '#2D6BE4';

  return createTheme({
    palette: {
      primary: {
        main: accent,
      },
      secondary: {
        main: isDark ? '#262626' : '#F1F5F4',
      },
      warning: {
        main: isDark ? '#FF9900' : '#B45309',
      },
      text: {
        primary: isDark ? '#F5F5F5' : '#1C1C1E',
        // The shared theme hardcodes this to '#fff' in BOTH modes (invisible
        // in light mode wherever it is actually used - see MSPPROC-17664's
        // Settings-screen fix). A real, working muted tone here instead.
        // Opacity chosen (not just "looks muted enough") to clear WCAG AA's
        // 4.5:1 text minimum against BOTH surface.page and surface.card in
        // this mode - verified by contrast-solve, not eyeballed. See
        // MSPPROC-17664.
        secondary: isDark ? 'rgba(255,255,255,0.58)' : 'rgba(0,0,0,0.58)',
        // A WCAG-AA-safe stand-in for primary.main wherever the accent
        // color is used as literal text (selected calendar day, selected
        // time slot, avatar initials, "Admin" chip, etc.) rather than an
        // icon/border/button fill. primary.main itself stays the same
        // literal value across modes (mobile-app parity) - this is
        // per-mode on purpose. Verified against the tightest real case,
        // not just a flat surface: several of these render on
        // topBar.activeBackground's tint rather than a flat card, which in
        // dark mode is the HARDER background (composited tint is lighter
        // than the flat card, cutting a naively-solved value's margin to
        // 4.56:1) - this value clears that same tint at 5.26:1.
        // See MSPPROC-17664.
        accent: isDark ? '#87ABEF' : '#1C5DDB',
      },
      background: {
        default: isDark ? '#1C1C1E' : '#F2F2F7',
      },
      surface: {
        page: isDark ? '#1C1C1E' : '#F2F2F7',
        card: isDark ? '#2C2C2E' : '#FFFFFF',
        cardShadow: isDark
          ? 'none'
          : '0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)',
        // A slightly-raised background for grouped rows inside a card
        // (mobile's `bg-section`) - between card and page, no border needed.
        section: isDark ? 'rgba(255,255,255,0.04)' : '#FAFAFA',
        borderCard: isDark ? 'rgba(255,255,255,0.11)' : 'rgba(0,0,0,0.08)',
        borderInput: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.14)',
      },
      // A quiet, neutral nav bar with a SOFT TINT (not a solid fill) marking
      // the active tab, and muted unselected icons - restraint does the work
      // that a bold color block did in v1, without reading as garish.
      topBar: {
        background: isDark ? '#1C1C1E' : '#FFFFFF',
        divider: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.09)',
        icon: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
        hover: isDark ? 'rgba(77,142,255,0.10)' : 'rgba(45,107,228,0.06)',
        activeBackground: isDark ? 'rgba(77,142,255,0.10)' : 'rgba(45,107,228,0.08)',
        activeIcon: accent,
      },
      // Tinted pill/badge colors, same values the mobile screen's status
      // pills use (Verified/Not-Verified, etc.) - see
      // extension/design/StatusBadge.tsx. The *Bg tints are unchanged; the
      // *Color text values are darkened/lightened (mode-appropriately) from
      // the original mobile-app values just enough to clear 4.5:1 against
      // their own bg - StatusBadge text is small (13px, not "large text"),
      // so it needs the full text-contrast bar. Verified via contrast-solve
      // against each bg as actually composited (light mode's *Bg are
      // already opaque; dark mode's are translucent over surface.card).
      // See MSPPROC-17664.
      status: {
        successBg: isDark ? 'rgba(16,185,129,0.2)' : '#D1FAE5',
        successColor: isDark ? '#49D8A4' : '#036547',
        dangerBg: isDark ? 'rgba(220,38,38,0.2)' : '#FEE2E2',
        dangerColor: isDark ? '#F98C8C' : '#B71D1D',
        warningBg: isDark ? 'rgba(255,153,0,0.10)' : 'rgba(180,83,9,0.09)',
        warningColor: isDark ? '#FFA31A' : '#9B4708',
        neutralBg: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
        // StatusBadge's 'neutral' tone actually renders with
        // theme.palette.text.secondary, not this field (nothing currently
        // reads neutralColor) - kept in sync with it regardless, so it's
        // never a landmine if something starts using it.
        neutralColor: isDark ? 'rgba(255,255,255,0.58)' : 'rgba(0,0,0,0.58)',
      },
      divider: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
      // Unused today (nothing in the codebase reads textSub) - kept in sync
      // with text.secondary's corrected, WCAG-AA-verified opacity rather
      // than left at its previous 2.1-2.5:1 value, so it isn't a silent
      // accessibility trap the moment something does use it. See
      // MSPPROC-17664.
      textSub: isDark ? 'rgba(255,255,255,0.58)' : 'rgba(0,0,0,0.58)',
      mode,
    },
    typography: {
      fontFamily: "'Nunito', sans-serif",
      h1: { fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.2 },
      h2: { fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.25 },
      h3: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.3 },
      h4: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.35 },
      h5: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 },
      h6: { fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.4 },
      body1: { fontSize: '0.9375rem', lineHeight: 1.5 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      button: { fontWeight: 700, textTransform: 'none' },
      caption: { fontSize: '0.75rem', lineHeight: 1.4 },
    },
    shape: {
      // Softer, more modern corners than MUI's default 4px - affects every
      // Button/Paper/Dialog/TextField/Card that doesn't set its own radius.
      borderRadius: 10,
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 700,
            paddingTop: 8,
            paddingBottom: 8,
            boxShadow: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            color: isDark ? '#F5F5F5' : '#1C1C1E',
            '& .MuiBreadcrumbs-li': {
              cursor: 'pointer',
              color: isDark ? '#F5F5F5' : '#1C1C1E',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: accent,
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: accent,
              opacity: 0.5,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: isDark ? '#F5F5F5' : '#1C1C1E',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontSize: '0.75rem',
            fontWeight: 600,
          },
        },
      },
    },
  });
};
